import React, { useEffect, useState } from 'react';
import * as qualidadesApi from '../../api/qualidades';
import { useEmpresaSelecionada } from '../../contexts/EmpresaContext';
import PageHeader from '../../components/common/PageHeader';
import EmptyState from '../../components/common/EmptyState';
import Skeleton from '../../components/common/Skeleton';
import SearchableSelect from '../../components/common/SearchableSelect';
import StatusBadge from '../../components/common/StatusBadge';
import { getErrorMessage } from '../../utils/apiError';

const VAZIO = { nome: '', ordem: 0 };

export default function Qualidades() {
  const { modoGlobal, empresas, idEmpresa } = useEmpresaSelecionada();

  const [itens, setItens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [form, setForm] = useState(VAZIO);
  const [idEmpresaForm, setIdEmpresaForm] = useState('');
  const [editando, setEditando] = useState(null);
  const [mostrarForm, setMostrarForm] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [mostrarInativos, setMostrarInativos] = useState(false);

  const carregar = () => {
    setLoading(true);
    qualidadesApi
      .listQualidades(mostrarInativos)
      .then((r) => setItens(r.data))
      .catch(() => setErro('Erro ao carregar classificações de qualidade.'))
      .finally(() => setLoading(false));
  };

  // idEmpresa muda quando o super_admin troca de empresa no seletor do topbar — sem isso, a
  // lista continuaria mostrando os cadastros da empresa anterior até uma navegação manual.
  useEffect(carregar, [mostrarInativos, idEmpresa]);

  const abrirNovo = () => {
    setForm({ nome: '', ordem: itens.length });
    setIdEmpresaForm('');
    setEditando(null);
    setMostrarForm(true);
  };

  const abrirEdicao = (item) => {
    setForm({ nome: item.nome, ordem: item.ordem });
    setEditando(item);
    setMostrarForm(true);
  };

  const alternarAtivo = async (item) => {
    const acao = item.ativo ? 'Desativar' : 'Reativar';
    const aviso = item.ativo ? ' Ela deixa de aparecer nesta lista e nos seletores de busca.' : '';
    if (!window.confirm(`${acao} "${item.nome}"?${aviso}`)) return;
    setErro('');
    try {
      await qualidadesApi.updateQualidade(item.id_qualidade, { ativo: !item.ativo }, item.id_empresa);
      carregar();
    } catch (err) {
      setErro(getErrorMessage(err, 'Erro ao alterar o status da classificação.'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErro('');
    if (!editando && modoGlobal && !idEmpresaForm) {
      setErro('Selecione a empresa dona deste cadastro.');
      return;
    }
    setSalvando(true);
    try {
      if (editando) await qualidadesApi.updateQualidade(editando.id_qualidade, form, editando.id_empresa);
      else await qualidadesApi.createQualidade(form, idEmpresaForm);
      setMostrarForm(false);
      carregar();
    } catch (err) {
      setErro(getErrorMessage(err, 'Erro ao salvar.'));
    } finally {
      setSalvando(false);
    }
  };

  const ordenadas = [...itens].sort((a, b) => a.ordem - b.ordem);

  return (
    <div>
      <PageHeader titulo="Classificações de Qualidade" subtitulo="Ex: Extra, Primeira, Segunda, Refugo — ordem define a melhor qualidade primeiro">
        {!mostrarForm && (
          <button type="button" className="sf-btn sf-btn-primary" onClick={abrirNovo}>
            <i className="bi bi-plus-lg" />
            Nova classificação
          </button>
        )}
      </PageHeader>

      {erro && (
        <div className="sf-login-alert mb-3">
          <i className="bi bi-exclamation-circle" />
          {erro}
        </div>
      )}

      {mostrarForm && (
        <form onSubmit={handleSubmit} className="sf-card p-4 mb-3" style={{ maxWidth: 480 }}>
          {editando && modoGlobal && (
            <div className="sf-badge sf-badge-neutral sf-badge-plain mb-3">
              <i className="bi bi-building" /> {editando.empresa.nome}
            </div>
          )}
          {!editando && modoGlobal && (
            <div className="sf-field-label mb-3">
              <span>Empresa</span>
              <SearchableSelect
                options={empresas.map((e) => ({ value: e.id_empresa, label: e.nome }))}
                value={idEmpresaForm}
                onChange={setIdEmpresaForm}
                placeholder="Selecione a empresa..."
                required
              />
            </div>
          )}
          <div className="row g-3">
            <div className="col-8">
              <label className="sf-field-label">
                <span>Nome</span>
                <span className="sf-field">
                  <input required value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} />
                </span>
              </label>
            </div>
            <div className="col-4">
              <label className="sf-field-label">
                <span>Ordem</span>
                <span className="sf-field">
                  <input type="number" value={form.ordem} onChange={(e) => setForm({ ...form, ordem: Number(e.target.value) })} />
                </span>
              </label>
            </div>
          </div>
          <div className="mt-3 d-flex gap-2">
            <button type="submit" className="sf-btn sf-btn-primary" disabled={salvando}>
              {salvando ? 'Salvando...' : 'Salvar'}
            </button>
            <button type="button" className="sf-btn sf-btn-ghost" onClick={() => setMostrarForm(false)}>
              Cancelar
            </button>
          </div>
        </form>
      )}

      <div className="sf-table-card">
        <div className="sf-table-toolbar">
          <label className="d-flex align-items-center gap-2" style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}>
            <input type="checkbox" checked={mostrarInativos} onChange={(e) => setMostrarInativos(e.target.checked)} />
            Mostrar inativas
          </label>
        </div>
        {loading ? (
          <div className="p-4">
            <Skeleton linhas={5} />
          </div>
        ) : ordenadas.length === 0 ? (
          <EmptyState icon="bi-award" titulo="Nenhuma classificação cadastrada" texto="Comece cadastrando a primeira classificação de qualidade." />
        ) : (
          <div className="table-responsive">
            <table className="sf-table">
              <thead>
                <tr>
                  <th>Ordem</th>
                  <th>Nome</th>
                  {modoGlobal && <th>Empresa</th>}
                  {mostrarInativos && <th>Status</th>}
                  <th />
                </tr>
              </thead>
              <tbody>
                {ordenadas.map((q) => (
                  <tr key={q.id_qualidade}>
                    <td>{q.ordem}</td>
                    <td>
                      <span className="d-flex align-items-center gap-2">
                        <span className="sf-cell-icon"><i className="bi bi-award" /></span>
                        <span className="sf-cell-title">{q.nome}</span>
                      </span>
                    </td>
                    {modoGlobal && <td className="sf-cell-sub">{q.empresa.nome}</td>}
                    {mostrarInativos && <td><StatusBadge status={q.ativo ? 'ativo' : 'inativo'} /></td>}
                    <td className="text-end">
                      <div className="d-flex gap-2 justify-content-end">
                        <button type="button" className="sf-btn sf-btn-ghost" onClick={() => abrirEdicao(q)} title="Editar">
                          <i className="bi bi-pencil" />
                        </button>
                        <button
                          type="button"
                          className="sf-btn sf-btn-ghost"
                          style={q.ativo ? { color: 'var(--sf-danger-fg)', borderColor: '#f0cfcc' } : undefined}
                          onClick={() => alternarAtivo(q)}
                          title={q.ativo ? 'Desativar' : 'Reativar'}
                        >
                          <i className={`bi ${q.ativo ? 'bi-slash-circle' : 'bi-check-circle'}`} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
